"use client";

import { IconAlertWarningOutline18, IconChevronDownOutline18, IconChevronUpOutline18, IconDownload2Outline18 } from "nucleo-ui-outline-18";
import type { ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import { useSimBalance, type SimBalanceItem } from "@/hooks/useSimTokenInfo";
import { getChainInfo, getNativeTokenDecimals } from "@/lib/chains";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { useWalletsStore } from "@/stores/walletsStore";
import { useModalStore } from "@b3dotfun/sdk/global-account/react";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useMemo, useState } from "react";
import { isAddress } from "viem";
import { isValidBlockchainAddress } from "@/hooks/useAddressValidation";
import { addressEquals } from "@/lib/utils/address";

/** Known token addresses (lowercased) to human-readable info */
const TOKEN_INFO_MAP: Record<string, {
  tokenSymbol: string;
  chainId: number;
}> = {
  // USDC variants
  "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48": {
    tokenSymbol: "USDC",
    chainId: 1
  },
  "0xaf88d065e77c8cc2239327c5edb3a432268e5831": {
    tokenSymbol: "USDC",
    chainId: 42161
  },
  "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913": {
    tokenSymbol: "USDC",
    chainId: 8453
  },
  "0x0b2c639c533813f4aa9d7837caf62653d097ff85": {
    tokenSymbol: "USDC",
    chainId: 10
  },
  "0x3c499c542cef5e3811e1192ce70d8cc03d5c3359": {
    tokenSymbol: "USDC",
    chainId: 137
  },
  // pUSD (Polymarket V2 collateral)
  "0xc011a7e12a19f7b1f670d46f03b03f3342e82dfb": {
    tokenSymbol: "pUSD",
    chainId: 137
  },
  // USDC.e (bridged)
  "0x2791bca1f2de4661ed88a30c99a7a9449aa84174": {
    tokenSymbol: "USDC.e",
    chainId: 137
  },
  "0xff970a61a04b1ca14834a43f5de4533ebddb5cc8": {
    tokenSymbol: "USDC.e",
    chainId: 42161
  }
};
interface ParsedBalanceError {
  tokenSymbol: string;
  chainId: number;
  chainName: string;
  tokenAddress: string;
}

/**
 * Parse INSUFFICIENT_BALANCE error details from the error string.
 * Handles both JSON-structured errors and plain-text error messages.
 *
 * Resolution order:
 * 1. Structured JSON `details.tokenAddress` lookup in TOKEN_INFO_MAP
 * 2. Structured JSON `details.chainId` + `details.tokenAddress` (use chainId directly)
 * 3. Text-based heuristics (keywords in the error message)
 * 4. Returns null if we can't confidently identify the token (no guessing)
 */
function parseInsufficientBalanceError(errorStr: string): ParsedBalanceError | null {
  if (!errorStr.includes("INSUFFICIENT_BALANCE")) return null;
  const lower = errorStr.toLowerCase();

  // Try JSON extraction first
  try {
    const jsonRegex = /\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g;
    const matches = errorStr.match(jsonRegex);
    if (matches) {
      for (const match of matches) {
        try {
          const parsed = JSON.parse(match);
          if (parsed.code === "INSUFFICIENT_BALANCE" || parsed.errorCode === "INSUFFICIENT_BALANCE") {
            const message = (parsed.message || parsed.error || "").toLowerCase();
            const details = parsed.details || {};
            const tokenAddr = (details.tokenAddress || "").toLowerCase();

            // 1. Known token address lookup
            const knownToken = tokenAddr ? TOKEN_INFO_MAP[tokenAddr] : undefined;
            if (knownToken) {
              return {
                tokenSymbol: knownToken.tokenSymbol,
                chainId: knownToken.chainId,
                chainName: getChainInfo(knownToken.chainId).name,
                tokenAddress: tokenAddr
              };
            }

            // 2. Use structured details.chainId when available
            if (tokenAddr && details.chainId) {
              const chainId = Number(details.chainId);
              if (!Number.isFinite(chainId) || chainId <= 0) {
                const resolved = resolveFromText(message, tokenAddr);
                if (resolved) return resolved;
                return null;
              }
              return {
                tokenSymbol: details.symbol || details.tokenSymbol || "Token",
                chainId,
                chainName: getChainInfo(chainId).name,
                tokenAddress: tokenAddr
              };
            }

            // 3. Text heuristics on the error message
            const resolved = resolveFromText(message, tokenAddr);
            if (resolved) return resolved;
          }
        } catch {
          // not valid JSON, continue
        }
      }
    }
  } catch {
    // JSON parsing failed entirely
  }

  // 3b. Plain-text fallback: infer from error message content
  const resolved = resolveFromText(lower, "");
  if (resolved) return resolved;

  // No confident match — return null instead of guessing
  return null;
}

/** Resolve token info from keywords in a message string */
function resolveFromText(text: string, tokenAddr: string): ParsedBalanceError | null {
  const resolve = (tokenSymbol: string, chainId: number, fallbackAddr: string): ParsedBalanceError => ({
    tokenSymbol,
    chainId,
    chainName: getChainInfo(chainId)?.name ?? String(chainId),
    tokenAddress: tokenAddr || fallbackAddr
  });
  if (text.includes("pusd") || text.includes("polymarket")) {
    return resolve("pUSD", 137, "0xc011a7e12a19f7b1f670d46f03b03f3342e82dfb");
  }
  if (text.includes("usdc.e") && text.includes("polygon")) {
    return resolve("USDC.e", 137, "0x2791bca1f2de4661ed88a30c99a7a9449aa84174");
  }
  if (text.includes("usdc.e") && text.includes("arbitrum")) {
    return resolve("USDC.e", 42161, "0xff970a61a04b1ca14834a43f5de4533ebddb5cc8");
  }
  if (text.includes("usdc") && text.includes("arbitrum")) {
    return resolve("USDC", 42161, "0xaf88d065e77c8cc2239327c5edb3a432268e5831");
  }
  if (text.includes("usdc") && text.includes("base")) {
    return resolve("USDC", 8453, "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913");
  }
  if (text.includes("usdc") && text.includes("polygon")) {
    return resolve("USDC", 137, "0x3c499c542cef5e3811e1192ce70d8cc03d5c3359");
  }
  if (text.includes("usdc") && text.includes("optimism")) {
    return resolve("USDC", 10, "0x0b2c639c533813f4aa9d7837caf62653d097ff85");
  }
  return null;
}

/**
 * Check if an error string contains an INSUFFICIENT_BALANCE error code.
 */
export function isInsufficientBalanceError(errorStr: string | undefined): boolean {
  if (!errorStr) return false;
  return errorStr.includes("INSUFFICIENT_BALANCE");
}

/**
 * Infer required tokens from a node's payload when error parsing can't identify them.
 * Checks for:
 * 1. Token being sent (tokenAddress + chainId) — looked up in TOKEN_INFO_MAP
 * 2. Gas token for the chain (chainId / srcChainId / dstChainId)
 */
function inferTokensFromPayload(payload: Record<string, unknown>): TokenEntry[] {
  const result: TokenEntry[] = [];
  const seen = new Set<string>();

  // Resolve the primary chainId
  const chainId = Number(payload.chainId || payload.srcChainId) || 0;

  // 1. Token being transferred (e.g. send-erc20-token, approve, etc.)
  const tokenAddr = (payload.tokenAddress as string || payload.tokenIn as string || "").toLowerCase();
  if (tokenAddr && chainId > 0) {
    const known = TOKEN_INFO_MAP[tokenAddr];
    if (known) {
      const key = `${known.chainId}-${tokenAddr}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({
          address: tokenAddr,
          chainId: known.chainId,
          symbol: known.tokenSymbol,
          chainName: getChainInfo(known.chainId).name,
          reason: "Token used by this action"
        });
      }
    }
  }

  // 2. Gas token for the chain
  if (chainId > 0) {
    const chain = getChainInfo(chainId);
    const gasKey = `${chainId}-gas`;
    if (!seen.has(gasKey)) {
      seen.add(gasKey);
      result.push({
        address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
        chainId,
        symbol: chain.gasToken,
        chainName: chain.name,
        reason: "Gas fees"
      });
    }
  }
  return result;
}

/**
 * Check if a token has sufficient balance in the wallet.
 * For ERC20 tokens: compares against the action's amount if available.
 * For gas tokens: checks if balance is > 0.
 */
function hasEnoughBalance(token: TokenEntry, balances: SimBalanceItem[], requiredAmount?: string | number): boolean {
  const isGas = isNativeTokenAddress(token.address);
  if (isGas) {
    // Gas: check native token balance on the chain
    const native = balances.find(b => b.chain_id === token.chainId && isNativeTokenAddress(b.address));
    if (!native) return false;
    return BigInt(native.amount || "0") > BigInt(0);
  }

  // ERC20: find the matching token balance
  const balance = balances.find(b => b.chain_id === token.chainId && addressEquals(b.address, token.address));
  if (!balance) return false;
  const balanceAmount = BigInt(balance.amount || "0");
  if (balanceAmount === BigInt(0)) return false;

  // If we have a required amount from the payload, compare against it
  if (requiredAmount !== undefined && requiredAmount !== "") {
    try {
      const decimals = balance.decimals || getNativeTokenDecimals(token.chainId);
      const amountStr = String(requiredAmount);
      // Parse human-readable amount (e.g. "0.01") into raw units
      const parts = amountStr.split(".");
      const wholePart = parts[0] || "0";
      const fracPart = (parts[1] || "").padEnd(decimals, "0").slice(0, decimals);
      const rawRequired = BigInt(wholePart + fracPart);
      return balanceAmount >= rawRequired;
    } catch {
      // Can't parse amount, just check > 0
      return true;
    }
  }
  return true;
}

/** Resolved token entry for display */
interface TokenEntry {
  address: string;
  chainId: number;
  symbol: string;
  chainName: string;
  reason?: string;
}
interface InsufficientBalanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  errorStr: string | undefined;
  definition?: ActionTriggerDefinition;
  /** Node payload — used to infer gas/token requirements when error parsing fails */
  nodePayload?: Record<string, unknown>;
}
export function InsufficientBalanceModal({
  open,
  onOpenChange,
  errorStr,
  definition,
  nodePayload
}: InsufficientBalanceModalProps) {
  const [showDetails, setShowDetails] = useState(false);
  const wallets = useWalletsStore(state => state.wallets);
  const defaultWallet = wallets.find(w => w.isDefault) || wallets[0];
  const setModalOpen = useModalStore(state_0 => state_0.setB3ModalOpen);
  const setModalContentType = useModalStore(state_1 => state_1.setB3ModalContentType);

  // Build candidate token list: prefer requiredTokens from definition, fallback to error parsing, then payload inference
  const candidateTokens: TokenEntry[] = useMemo(() => {
    if (definition?.requiredTokens && definition.requiredTokens.length > 0) {
      return definition.requiredTokens.map(t => ({
        address: t.address,
        chainId: t.chainId,
        symbol: t.symbol,
        chainName: getChainInfo(t.chainId).name,
        reason: t.reason
      }));
    }
    // Fallback: parse from error string
    const parsed = parseInsufficientBalanceError(errorStr || "");
    if (parsed) {
      return [{
        address: parsed.tokenAddress,
        chainId: parsed.chainId,
        symbol: parsed.tokenSymbol,
        chainName: parsed.chainName
      }];
    }
    // Last resort: infer from node payload
    if (nodePayload) {
      return inferTokensFromPayload(nodePayload);
    }
    return [];
  }, [definition?.requiredTokens, errorStr, nodePayload]);

  // Collect chain IDs we need balances for
  const fetchChainIds = useMemo(() => {
    const ids = new Set<number>();
    for (const t_0 of candidateTokens) {
      if (t_0.chainId > 0) ids.add(t_0.chainId);
    }
    return ids.size > 0 ? [...ids] : undefined;
  }, [candidateTokens]);

  // Fetch wallet balances to filter out tokens the user already has enough of
  const walletAddress = defaultWallet?.address;
  const shouldFetch = open && candidateTokens.length > 1 && !!walletAddress && !!fetchChainIds;
  const {
    data: balanceData,
    isLoading: isLoadingBalances
  } = useSimBalance(walletAddress, fetchChainIds, shouldFetch);

  // Required amount from the payload (for ERC20 comparison)
  const requiredAmount = useMemo(() => {
    if (!nodePayload) return undefined;
    return (nodePayload.amount ?? nodePayload.value) as string | number | undefined;
  }, [nodePayload]);

  // Filter to only show tokens the wallet is actually missing
  const tokens: TokenEntry[] = useMemo(() => {
    // If only 1 candidate or no balance data yet, show all candidates
    if (candidateTokens.length <= 1 || !balanceData?.balances) {
      return candidateTokens;
    }
    const insufficient = candidateTokens.filter(token => !hasEnoughBalance(token, balanceData.balances, token.address ? requiredAmount : undefined));

    // If all tokens show as sufficient (shouldn't happen since we got an error), show all
    return insufficient.length > 0 ? insufficient : candidateTokens;
  }, [candidateTokens, balanceData, requiredAmount]);
  const handleDeposit = useCallback((token_0: TokenEntry) => {
    if (!defaultWallet?.address || !isValidBlockchainAddress(defaultWallet.address)) return;
    onOpenChange(false);
    setModalContentType({
      type: "anySpendDeposit",
      recipientAddress: defaultWallet.address,
      destinationTokenAddress: token_0.address,
      destinationTokenChainId: token_0.chainId,
      actionLabel: `deposit ${token_0.symbol} on ${token_0.chainName}`,
      allowDirectTransfer: true,
      showFiatOption: false
    });
    setModalOpen(true);
  }, [defaultWallet?.address, onOpenChange, setModalContentType, setModalOpen]);
  return <ResponsiveModal open={open} onOpenChange={onOpenChange} title="Insufficient Balance" contentClass="max-w-sm overflow-hidden rounded-2xl border border-ds-border-default p-0">
      <div className="flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pb-3 pt-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/40">
            <IconAlertWarningOutline18 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="body-sm-semibold">Insufficient Balance</h4>
            <p className="label-xs">Fund the required tokens to run this action.</p>
          </div>
        </div>

        {/* Token list */}
        <div className="px-5 pb-4 pt-2">
          <p className="micro-label mb-3">Required Tokens</p>
          {isLoadingBalances ? <div className="caption-xs flex items-center gap-2 py-4">
              <PixelLoader size="xs" />
              Checking balances...
            </div> : tokens.length === 0 ? <p className="caption-xs">Unable to identify required token. Check your wallet balance and try again.</p> : <div className="space-y-2">
              {tokens.map(token_1 => <div key={`${token_1.chainId}-${token_1.address}`} className="border-ds-neutral-100 dark:border-ds-neutral-700/60 flex items-center justify-between rounded-xl border px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5">
                      <span className="body-sm-semibold">{token_1.symbol}</span>
                      <span className="caption-xs">on {token_1.chainName}</span>
                    </div>
                    {token_1.reason && <p className="caption-xs mt-0.5">{token_1.reason}</p>}
                  </div>
                  <button onClick={() => handleDeposit(token_1)} className="ml-3 flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700">
                    <IconDownload2Outline18 className="h-3 w-3" />
                    Deposit
                  </button>
                </div>)}
            </div>}
        </div>

        {/* View Details toggle */}
        {errorStr && <div className="border-ds-neutral-100 dark:border-ds-neutral-700/60 border-t px-5 py-3">
            <button onClick={() => setShowDetails(prev => !prev)} className="text-ds-text-disabled hover:text-ds-text-subtle dark:text-ds-text-muted flex items-center gap-1 text-xs transition-colors dark:hover:text-zinc-300">
              {showDetails ? <IconChevronUpOutline18 className="h-3 w-3" /> : <IconChevronDownOutline18 className="h-3 w-3" />}
              {showDetails ? "Hide details" : "View details"}
            </button>
            {showDetails && <pre className="text-ds-text-subtle dark:bg-ds-neutral-800 dark:text-ds-text-disabled text-2xs mt-2 max-h-32 overflow-auto rounded-lg bg-zinc-50 p-3">
                {errorStr}
              </pre>}
          </div>}
      </div>
    </ResponsiveModal>;
}